import React, { useEffect, useRef } from 'react';

const JumpingText = ({ text }) => {
  const containerRef = useRef(null);
  const ballRef = useRef(null);
  const words = text.split(' ');

  useEffect(() => {
    const container = containerRef.current;
    const ball = ballRef.current;
    if (!container || !ball) return;

    let animationFrameId;
    let startTime = null;
    let cachedPositions = [];
    let prevActiveIndex = -1;

    // Animation configuration
    const bounceDuration = 450; // Slower bounce (450ms per letter jump)
    const highJumpDuration = 1500; // Slower return jump (1.5s to fly back to start)
    const peakHeight = 32; // Higher arc for slower letter-to-letter bounces
    const highJumpHeight = 80; // Higher arc for slower return jump

    const updatePositions = () => {
      const letters = Array.from(container.querySelectorAll('.jumping-letter-span'));
      if (letters.length === 0) return;
      const containerRect = container.getBoundingClientRect();
      cachedPositions = letters.map((el) => {
        const rect = el.getBoundingClientRect();
        return {
          x: rect.left - containerRect.left + rect.width / 2,
          y: rect.top - containerRect.top,
          element: el
        };
      });
    };

    // Calculate positions once upon layout and only recalculate on resize
    updatePositions();
    let resizeObserver = null;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => {
        updatePositions();
      });
      resizeObserver.observe(container);
    }

    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const elapsed = timestamp - startTime;

      if (cachedPositions.length === 0) {
        updatePositions();
        if (cachedPositions.length === 0) {
          animationFrameId = requestAnimationFrame(animate);
          return;
        }
      }

      const N = cachedPositions.length;
      const totalBounceTime = (N - 1) * bounceDuration;
      const cycleDuration = totalBounceTime + highJumpDuration;
      const cycleElapsed = elapsed % cycleDuration;

      let ballX = 0;
      let ballY = 0;
      let activeIndex = -1;

      if (cycleElapsed < totalBounceTime) {
        // Forward bouncing phase
        const step = cycleElapsed / bounceDuration;
        const index = Math.floor(step);
        const t = step - index; // progress in current bounce: [0, 1]

        const startPos = cachedPositions[index];
        const endPos = cachedPositions[index + 1];

        if (startPos && endPos) {
          // Linear horizontal slide
          ballX = startPos.x + t * (endPos.x - startPos.x);
          // Parabolic vertical arc
          ballY = -4 * peakHeight * t * (1 - t);

          // Mark active landing indexes (at landing start and landing end)
          if (t < 0.15) {
            activeIndex = index;
          } else if (t > 0.85) {
            activeIndex = index + 1;
          }
        }
      } else {
        // High jump reset phase
        const t = (cycleElapsed - totalBounceTime) / highJumpDuration; // progress: [0, 1]
        const startPos = cachedPositions[N - 1];
        const endPos = cachedPositions[0];

        if (startPos && endPos) {
          ballX = startPos.x + t * (endPos.x - startPos.x);
          ballY = -4 * highJumpHeight * t * (1 - t);
        }
      }

      // Move the ball (subtracting radius of 5px to center it) using GPU compositor translate3d
      const ballRadius = 5;
      ball.style.transform = `translate3d(${ballX - ballRadius}px, ${ballY - ballRadius}px, 0)`;

      // Only update DOM classes when active index actually changes
      if (activeIndex !== prevActiveIndex) {
        if (prevActiveIndex >= 0 && cachedPositions[prevActiveIndex]) {
          cachedPositions[prevActiveIndex].element.classList.remove('squashed');
        }
        if (activeIndex >= 0 && cachedPositions[activeIndex]) {
          cachedPositions[activeIndex].element.classList.add('squashed');
        }
        prevActiveIndex = activeIndex;
      }

      animationFrameId = requestAnimationFrame(animate);
    };

    animationFrameId = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(animationFrameId);
      resizeObserver.disconnect();
    };
  }, [text]);

  let charCount = 1;
  return (
    <span className="jumping-text-container" ref={containerRef}>
      {/* The Bouncing Ball */}
      <span className="bouncing-ball" ref={ballRef}></span>
      
      {words.map((word, wIdx) => {
        const wordChars = word.split('');
        return (
          <span key={wIdx} className="jumping-word" style={{ display: 'inline-block', whiteSpace: 'nowrap' }}>
            {wordChars.map((char) => {
              const currentCount = charCount++;
              return (
                <span 
                  key={currentCount} 
                  className="jumping-letter-span"
                >
                  {char}
                </span>
              );
            })}
            {wIdx < words.length - 1 && <span className="jumping-space">&nbsp;</span>}
          </span>
        );
      })}
    </span>
  );
};

export default JumpingText;
